import { useQuery } from "@tanstack/react-query";
import { reportService } from "../../services/reports";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { IndianRupee, FileText, TrendingUp, Users } from "lucide-react";

const MetricCard = ({ icon: Icon, label, value, subtext, color }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-xl font-bold text-gray-900">{value}</p>
        {subtext && <p className="text-xs text-gray-400">{subtext}</p>}
      </div>
    </div>
  </div>
);

const RevenueReport = ({ filters }) => {
  const { data } = useQuery({
    queryKey: ["revenue-report", filters],
    queryFn: () => reportService.getRevenueReport(filters),
  });

  if (!data) return <div className="text-center py-8 text-gray-400">Loading...</div>;

  const STATUS_COLORS = { paid: "#10b981", partial: "#f59e0b", pending: "#3b82f6", overdue: "#ef4444" };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard icon={IndianRupee} label="Total Revenue" value={`₹${(data.total || 0).toLocaleString()}`} color="bg-blue-500" />
        <MetricCard icon={TrendingUp} label="Collected" value={`₹${(data.paid || 0).toLocaleString()}`} color="bg-emerald-500" />
        <MetricCard icon={FileText} label="Invoices" value={data.invoiceCount || 0} subtext={`₹${(data.pending || 0).toLocaleString()} pending`} color="bg-purple-500" />
        <MetricCard icon={Users} label="Clients" value={data.totalClients || 0} subtext={`${data.activeProjects || 0} active projects`} color="bg-amber-500" />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Collection Progress</h3>
        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
          <span>₹{(data.paid || 0).toLocaleString()} of ₹{(data.total || 0).toLocaleString()}</span>
          <span className="font-medium">{data.total > 0 ? ((data.paid / data.total) * 100).toFixed(1) : 0}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-500" style={{ width: `${data.total > 0 ? (data.paid / data.total) * 100 : 0}%` }} />
        </div>
      </div>

      {data.monthlyRevenue?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Monthly Revenue</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.monthlyRevenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(value) => `₹${value.toLocaleString()}`} />
              <Legend />
              <Bar dataKey="revenue" name="Invoiced" fill="#3b82f6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="collected" name="Collected" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {data.byStatus?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Invoices by Status</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {data.byStatus.map((item) => (
              <div key={item._id} className="rounded-xl p-4 text-center bg-gray-50">
                <div className="w-3 h-3 rounded-full mx-auto mb-2" style={{ backgroundColor: STATUS_COLORS[item._id] || "#6b7280" }} />
                <p className="text-2xl font-bold text-gray-900">{item.count}</p>
                <p className="text-sm capitalize text-gray-600">{item._id}</p>
                <p className="text-xs text-gray-400 mt-1">₹{(item.amount || 0).toLocaleString()}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RevenueReport;
